import * as THREE from '@phdsoft/three';
import { Injectable } from '@angular/core';
import { Vector3 } from '@phdsoft/three';
import { PointsRender } from '../utmPoints/graphics-render-pointShape';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Graphics } from './graphics';
import { DecalSelectionService } from '../selection/decalSelection';
import { Asset } from 'src/app/platform/busines-data/Framework/Asset';
import { GeometryUtil } from 'src/app/geometry-utils';

@Injectable({ providedIn: 'root' })
export class GraphicsRenderDecal {

    constructor(
      private pointsRender: PointsRender,
      private decalSelection: DecalSelectionService,
      private graphics: Graphics,
    ) { }

    private mousePosition: THREE.Vector3;

    private initSelectionDecal(asset: Asset) {
        this.decalSelection.setAsset(asset);
        this.decalSelection.setCamera(this.graphics.camera);
    }

    public copyElementGeometry(event, asset?: Asset, elementType?: string, currentSectionId?: string) {
        this.initSelectionDecal(asset);
        return this.updateMousePositionCopyGeometry(event, asset, elementType, currentSectionId);
    }

    public insertPlatePoint(event, box?: THREE.Box3, asset?: Asset, currentSectionId?: string, pointNormal?: any) {
        this.initSelectionDecal(asset);
        return this.insertPointOnPosition(event, 'Plate', box, asset, currentSectionId, false, pointNormal);
    }

    public insertStiffenerPoint(event, asset?: Asset, currentSectionId?: string) {
        this.initSelectionDecal(asset);
        return this.insertPointOnPosition(event, 'Stiffener', null, asset, currentSectionId);
    }

    public insertFacePlatePoint(event, asset?: Asset, currentSectionId?: string) {
        this.initSelectionDecal(asset);
        return this.insertPointOnPosition(event, 'FacePlate', null, asset, currentSectionId);
    }

    public insertBracketPoint(event, asset?: Asset, currentSectionId?: string) {
        this.initSelectionDecal(asset);
        return this.insertPointOnPosition(event, 'Bracket', null, asset, currentSectionId);
    }

    public insertBeamPartPoint(event, box?: THREE.Box3, asset?: Asset, currentSectionId?: string) {
        this.initSelectionDecal(asset);
        return this.insertPointOnPosition(event, 'BeamPart', box, asset, currentSectionId);
    }

    public insertCablePoint(event, box?: THREE.Box3, asset?: Asset, currentSectionId?: string) {
        this.initSelectionDecal(asset);
        return this.insertPointOnPosition(event, 'Cable', box, asset, currentSectionId);
    }

    public insertPipePoint(event, box?: THREE.Box3, asset?: Asset, currentSectionId?: string) {
      this.initSelectionDecal(asset);
      return this.insertPointOnPosition(event, 'Pipe', box, asset, currentSectionId);
    }

    /**
     * Raycast probe of the element the event points at — the exact raycast the point
     * insertion performs, without inserting anything. Used by the pivot axis lock to
     * correct the click on the clicked element's own surface.
     */
    public probeInsertionPoint(event, type: string, asset?: Asset, currentSectionId?: string) {
        this.initSelectionDecal(asset);
        const e = event.changedTouches ? event.changedTouches[0] : event;
        this.mousePosition = new THREE.Vector3((((e.clientX ? e.clientX : e.center.x) - this.graphics.offsetX) / window.innerWidth) * 2 - 1,
            -(((e.clientY ? e.clientY : e.center.y) - this.graphics.offsetY) / window.innerHeight) * 2 + 1, 0.5);
        return this.getPointOverMouse(type, asset, this.graphics.camera, currentSectionId);
    }

    public insertIconElement(event, box?: THREE.Box3, asset?: Asset, currentSectionId?: string) {
        this.initSelectionDecal(asset);
        return this.insertPointOnPosition(event, '', box, asset, currentSectionId, true);
    }

    public createIconFromPosition(points, target, sectionNormal?: THREE.Vector3, sectionBox?: THREE.Box3) {
        const point = new THREE.Vector3(points[0], points[1], points[2]);
        return this.pointsRender.returnIconPoint(point, target, sectionNormal, true, sectionBox)
    }

    public createPlatePointFromPosition(points, target, type, scope?, sectionNormal?: any, sectionBox?: THREE.Box3) {
        let _point = null;
        const point = new THREE.Vector3(points[0], points[1], points[2]);
        let normal = null;
        switch (type) {
            case 'Plate':
                {
                    {
                        _point = { point: this.pointsRender.returnPlatePoint(point, target, sectionNormal, true, sectionBox/*section.bbox*/), target: target };
                    }
                }

                break;
            case 'Stiffener':
                {
                    {
                        if (scope === 'ONLY WEB') {
                            _point = { point: this.pointsRender.returnWebPoint(point, target, sectionNormal, false), target: target };
                        }
                        else if (scope === 'ONLY FLANGE') {
                            _point = { point: this.pointsRender.returnFlangePoint(point, target, sectionNormal, false), target: target };
                        }
                    }
                }
                break;
            case 'Bracket':
                {
                    {
                        if (scope === 'ONLY WEB') {
                            _point = { point: this.pointsRender.returnWebPoint(point, target, sectionNormal), target: target };
                        }
                        else if (scope === 'ONLY FLANGE') {
                            _point = { point: this.pointsRender.returnFlangePoint(point, target, sectionNormal, false), target: target };
                        }
                    }
                }
                break;
            case 'FacePlate':
                {
                    {
                        _point = { point: this.pointsRender.returnFacePlatePoint(point, target, sectionNormal, false), target: target };
                    }
                }
                break;
            case 'BeamPart':
                {
                    {
                        _point = { point: this.pointsRender.returnBeamPartPoint(point, target, sectionNormal, false, sectionBox), target: target };
                    }
                }
                break;
            case 'Cable':
                {
                    {
                        _point = { point: this.pointsRender.returnCablePoint(point, target, sectionNormal, false, sectionBox), target: target };
                    }
                }
                break;
            case 'Pipe':
                {
                    {
                      let diameter = target?.properties?.Geometry?.Diameter
                      if (diameter == null){
                        diameter = target?.property?.Geometry?.Diameter
                      }
                        _point = { point: this.pointsRender.returnPipePoint(point, sectionNormal, diameter), target: target };
                    }
                }
                break;
            default:
                break;
        }

        return _point;
    }

    private insertPointOnPosition(event, type, box?: THREE.Box3, asset?: Asset, currentSectionId?: string, iconElementInsertion = false, pointNormal?: any) {
        event = event.changedTouches ? event.changedTouches[0] : event;
        this.mousePosition = new THREE.Vector3((((event.clientX ? event.clientX : event.center.x) - this.graphics.offsetX) / window.innerWidth) * 2 - 1,
            -(((event.clientY ? event.clientY : event.center.y) - this.graphics.offsetY) / window.innerHeight) * 2 + 1, 0.5);
        const pointData = this.getPointOverMouse(type, asset, this.graphics.camera, currentSectionId);
        let _points = [];
        if (!pointData) return _points;
        if (iconElementInsertion) { // icon element insertion
            let _point = { point: this.pointsRender.returnIconPoint(pointData.point, pointData.target, pointData.normal, true, box), target: pointData.target };
            _points.push(_point);
        } else { // inspection point insertion
            switch (type) {
                case 'Plate':
                    var normalNormalized = pointData.normal.normalize();
                    pointNormal.normal = normalNormalized.clone();
                    //   var arrayNormal = [normalNormalized.x, normalNormalized.y, normalNormalized.z];
                    let _point = { point: this.pointsRender.returnPlatePoint(pointData.point, pointData.target, normalNormalized, true, box), target: pointData.target };
                    _points.push(_point);
                    break;
                case 'Stiffener':
                    {
                        if (pointData.property) {
                            let adjustedPoint = null;
                            let start;
                            let end;
                            if (pointData.target.type === 'Line2') {
                                let target = pointData.target as Line2;
                                let geometry = target.geometry as LineGeometry;
                                let startend = geometry.attributes.instanceEnd.array;
                                start = new THREE.Vector3(startend[0], startend[1], startend[2]);
                                end = new THREE.Vector3(startend[3], startend[4], startend[5]);
                                let line3 = new THREE.Line3(start, end);
                                pointData.point = line3.closestPointToPoint(pointData.point, false, pointData.point);
                            }
                            if (pointData.property.Geometry?.WebThickness > 0) {
                                let _pointw = { point: this.pointsRender.returnWebPoint(pointData.point, pointData.target, pointData.normal), target: pointData.target };
                                _points.push(_pointw)
                            }
                            if (pointData.property.Geometry?.FlangeThickness > 0) {
                                let _pointf = { point: this.pointsRender.returnFlangePoint(pointData.point, pointData.target, pointData.normal), target: pointData.target };
                                _points.push(_pointf)
                            }
                        }
                    }

                    break;
                case 'Bracket':
                    {
                        if (pointData.property) {
                            let adjustedPoint = null;
                            let start;
                            let end;
                            if (pointData.target.type === 'Line2') {
                                let target = pointData.target as Line2;
                                let geometry = target.geometry as LineGeometry;
                                let startend = geometry.attributes.instanceEnd.array;
                                start = new THREE.Vector3(startend[0], startend[1], startend[2]);
                                end = new THREE.Vector3(startend[3], startend[4], startend[5]);
                                let line3 = new THREE.Line3(start, end);
                                pointData.point = line3.closestPointToPoint(pointData.point, false, pointData.point);
                            }
                            if (pointData.property.Geometry?.WebThickness > 0) {
                                let _pointw = { point: this.pointsRender.returnWebPoint(pointData.point, pointData.target, pointData.normal), target: pointData.target };
                                _points.push(_pointw)
                            }
                            if (pointData.property.Geometry?.FlangeThickness > 0) {
                                let _pointf = { point: this.pointsRender.returnFlangePoint(pointData.point, pointData.target, pointData.normal), target: pointData.target };
                                _points.push(_pointf)
                            }
                        }
                    }
                    break;
                case 'FacePlate':
                    {
                        if (pointData.property) {
                            let adjustedPoint = null;
                            let start;
                            let end;
                            if (pointData.target.type === 'Line2') {
                                let target = pointData.target as Line2;
                                let geometry = target.geometry as LineGeometry;
                                let startend = geometry.attributes.instanceEnd.array;
                                start = new THREE.Vector3(startend[0], startend[1], startend[2]);
                                end = new THREE.Vector3(startend[3], startend[4], startend[5]);
                                let line3 = new THREE.Line3(start, end);
                                pointData.point = line3.closestPointToPoint(pointData.point, false, pointData.point);
                            }
                            let _point = { point: this.pointsRender.returnFacePlatePoint(pointData.point, pointData.target, pointData.normal, true), target: pointData.target };
                            _points.push(_point);
                        }
                    }
                    break;
                case 'BeamPart':
                    {
                        let _point = { point: this.pointsRender.returnBeamPartPoint(pointData.point, pointData.target, pointData.normal, true, box), target: pointData.target };
                        _points.push(_point);
                    }
                    break;
                case 'Cable':
                    {
                        let _point = { point: this.pointsRender.returnCablePoint(pointData.point, pointData.target, pointData.normal, true, box), target: pointData.target };
                        _points.push(_point);
                    }
                    break;
                case 'Pipe':
                    {
                      let pipePoints = this.processPipePoint(pointData, asset, box)
                      for (const pipePoint of pipePoints){
                        _points.push(pipePoint)
                      }
                    }
                    break;
                default:
                    break;
            }
        }

        return _points;

    }

    public updateMousePositionCopyGeometry(event, asset?: Asset, elementType?: string, currentSectionId?: string) {
        event = event.changedTouches ? event.changedTouches[0] : event;
        this.mousePosition = new THREE.Vector3((((event.clientX ? event.clientX : event.center.x) - this.graphics.offsetX) / window.innerWidth) * 2 - 1,
            -(((event.clientY ? event.clientY : event.center.y) - this.graphics.offsetY) / window.innerHeight) * 2 + 1, 0.5);
        {
            return this.getGeometryOverMouse(elementType, currentSectionId);
        }
    }

    private getPointOverMouse(type: string, asset?: Asset, camera?: THREE.Camera, currentSectionId?: string) {
        this.decalSelection.setAsset(asset);
        this.decalSelection.setCamera(this.graphics.camera);
        const _intersection = this.decalSelection.elementsUnderMouse(this.mousePosition, type, currentSectionId);
        // elementsUnderMouse returns null/undefined when the ray hits nothing or nothing
        // passes the type/section filters — callers rely on a null probe, not a throw.
        if (_intersection && _intersection.intersected != null) {
            return {
                target: _intersection.intersection.object, point: _intersection.intersection.point,
                normal: _intersection.intersection.normal, property: _intersection.intersection.property
            };
        }
        return null;
    }

    private getGeometryOverMouse(elementType?: string, currentSectionId?: string) {
        const _intersection = this.decalSelection.elementsUnderMouse(this.mousePosition, elementType, currentSectionId);
        if (_intersection && _intersection.intersected != null) {
            return { target: _intersection.intersection.object };
        }
        return null;
    }

    public processPipePoint(pointData, asset: Asset, box: THREE.Box3): any[] {
      const surfaceElement = asset.StructureCampaigns[0].SurfaceElements.find(e => e.Guid == pointData.target.name)
      if (surfaceElement && surfaceElement.Line.length >= 6) {
        const i = surfaceElement.Line.length

        const start = new THREE.Vector3(surfaceElement.Line[0], surfaceElement.Line[1], surfaceElement.Line[2])
        const end = new THREE.Vector3(surfaceElement.Line[i-3], surfaceElement.Line[i-2], surfaceElement.Line[i-1])
        const line = new THREE.Line3(start, end);

        let ringPosition = new THREE.Vector3()
        line.closestPointToPoint(pointData.point, false, ringPosition);

        const radious = 0.00105 * pointData.property.Geometry.Diameter / 2;
        const axis = GeometryUtil.getRingPrincipalAxis(line)
        const axisX = axis[0]
        const axisY = axis[1]

        const selectedAngles: number[] = []

        for (const angle in asset.pipeInformation){
          if (asset.pipeInformation[angle]){
            selectedAngles.push(Number(angle))
          }
        }

        let pipePoints: Vector3[] = []
        for (const angle of selectedAngles){
          const point = GeometryUtil.getRingPoint(angle, ringPosition, axisX, axisY, radious)
          pipePoints.push(point)
        }

        var points = []
        for (const pipePoint of pipePoints){
          const normal = pipePoint.clone().sub(ringPosition).normalize()
          let _point = { point: this.pointsRender.returnPipePoint(pipePoint, normal, pointData.property.Geometry.Diameter), target: pointData.target };
          points.push(_point);
        }
        return points
      }
    }
}
